import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Form, Alert, InputGroup, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { authService } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import logger from '../utils/logger';

const APITokenManagement = () => {
  const { user, isAdmin } = useAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [clientId, setClientId] = useState('');

  // Fetch client data on component mount
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);

        // Fetch clients directly from the database
        const response = await api.get('/clients/user-client');

        if (response.data && response.data.success) {
          const clientsData = response.data.data;

          // Just use the first client (assuming one client per user)
          if (clientsData.length > 0) {
            const client = clientsData[0];
            setClientData(client);
            setClientId(client.clientId);

            logger.info('Client data fetched successfully');
          } else {
            throw new Error('No clients found for your account');
          }
        } else {
          throw new Error(response.data?.message || 'Failed to fetch client data');
        }
      } catch (err) {
        logger.logError('Client Data Fetch', err);
        setError(err.message || 'Failed to fetch client data');
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [user]);

  // API Token generation
  const handleGenerateToken = async () => {
    setIsGenerating(true);
    setError('');
    setSuccess('');

    try {
      // For admin users, show different message
      if (isAdmin) {
        setSuccess('As an admin, you have direct API access without needing to generate a token');
        setToken('Admin users have privileged access');
        setIsGenerating(false);
        return;
      }

      if (!clientId) {
        throw new Error('Client ID not found. Please contact support.');
      }

      if (!clientSecret.trim()) {
        throw new Error('Please enter your client secret to generate an API token');
      }

      const response = await api.post('/auth/generate-token', {
        clientId,
        clientSecret
      });

      const generatedToken = response.data.data.token;
      setToken(generatedToken);
      setSuccess('API token generated successfully');

      // SECURITY FIX: Remove the localStorage line
      // localStorage.setItem('apiToken', generatedToken);

      // Clear the secret field after successful generation for security
      setClientSecret('');

      logger.info('API token generated');

      // Refresh client data after token generation
      await refreshClientData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to generate API token');
      logger.logError('API Token Generation', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Refresh client data after actions
  const refreshClientData = async () => {
    try {
      if (!clientId) return;

      const response = await api.get(`/clients/status/${clientId}`);
      if (response.data && response.data.success) {
        setClientData(response.data.data);
      }
    } catch (err) {
      logger.logError('Client Data Refresh', err);
    }
  };

  const handleCopyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      setSuccess('API token copied to clipboard');
      logger.info('API token copied to clipboard');
    }
  };

  const handleCopyClientId = () => {
    if (clientId) {
      navigator.clipboard.writeText(clientId);
      setSuccess('Client ID copied to clipboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Calculate percentage of usage quota
  const calculateUsagePercentage = () => {
    if (!clientData) return 0;
    return Math.min(100, Math.round((clientData.usageCount / clientData.usageQuota) * 100));
  };

  // Determine progress bar color based on usage
  const getProgressBarColor = () => {
    const percentage = calculateUsagePercentage();
    if (percentage < 50) return 'success';
    if (percentage < 80) return 'warning';
    return 'danger';
  };

  return (
    <div className="modern-api-management">
      {/* Modern Header */}
      <div className="modern-page-header">
        <div className="header-glass-bg"></div>
        <div className="header-content">
          <div className="header-icon-wrapper">
            <div className="header-main-icon">
              <i className="bi bi-key-fill"></i>
            </div>
            <div className="header-text-content">
              <h1 className="page-title">API Management</h1>
              <p className="page-subtitle">Manage your API credentials and monitor usage</p>
            </div>
          </div>

          {isAdmin && (
            <div className="admin-badge">
              <i className="bi bi-shield-check"></i>
              <span>Admin Access</span>
            </div>
          )}
        </div>
      </div>

      <div className="modern-content-wrapper">
        {/* Modern Alerts */}
        {error && (
          <div className="modern-alert error">
            <div className="alert-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <div className="alert-content">
              <strong>Error:</strong> {error}
            </div>
            <button className="alert-close" onClick={() => setError('')}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        {success && (
          <div className="modern-alert success">
            <div className="alert-icon">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="alert-content">
              <strong>Success:</strong> {success}
            </div>
            <button className="alert-close" onClick={() => setSuccess('')}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        {loading ? (
          <div className="modern-loading-card">
            <div className="modern-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="loading-text">Loading your API credentials...</p>
          </div>
        ) : (
          <div className="modern-cards-grid">
            {/* API Keys Card */}
            <div className="modern-card main-card">
              <div className="card-header">
                <div className="card-header-content">
                  <div className="card-icon">
                    <i className="bi bi-key"></i>
                  </div>
                  <div>
                    <h3>API Credentials</h3>
                    <p>Your unique identifiers for API access</p>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {clientData ? (
                  <>
                    {/* Client Credentials Section */}
                    <div className="credentials-section">
                      <div className="section-header">
                        <h4>Client Credentials</h4>
                        <div className="status-indicator">
                          <div className={`status-dot ${clientData.status === 'active' ? 'success' :
                            clientData.status === 'pending' ? 'warning' : 'danger'}`}></div>
                          <span className="status-text">{clientData.status}</span>
                        </div>
                      </div>

                      <div className="credentials-grid">
                        <div className="credential-item">
                          <label className="modern-label">Client ID</label>
                          <div className="modern-input-group">
                            <input
                              type="text"
                              value={clientId}
                              readOnly
                              className="modern-input readonly"
                            />
                            <button
                              className="copy-btn"
                              onClick={handleCopyClientId}
                              title="Copy Client ID"
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        </div>

                        <div className="credential-item">
                          <label className="modern-label">Client Secret</label>
                          <div className="modern-input-group">
                            <input
                              type={showSecret ? "text" : "password"}
                              placeholder="Enter your client secret here"
                              value={clientSecret}
                              onChange={(e) => setClientSecret(e.target.value)}
                              className="modern-input"
                            />
                            <button
                              className="toggle-btn"
                              onClick={() => setShowSecret(!showSecret)}
                              title={showSecret ? "Hide secret" : "Show secret"}
                            >
                              <i className={`bi ${showSecret ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                            </button>
                          </div>
                          <small className="input-help">
                            Your client secret is required to generate API tokens and is never stored.
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Token Generation Section */}
                    <div className="token-section">
                      <div className="section-header">
                        <h4>API Token</h4>
                      </div>

                      <p className="section-description">
                        Generate a secure token to authenticate your API requests. Tokens are temporary and should be regenerated regularly.
                      </p>

                      <div className="token-actions">
                        <button
                          className={`generate-btn ${clientData?.status !== 'active' ? 'disabled' : ''}`}
                          onClick={handleGenerateToken}
                          disabled={isGenerating || !clientSecret.trim() || clientData?.status !== 'active'}
                        >
                          {isGenerating ? (
                            <>
                              <div className="btn-spinner"></div>
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <i className="bi bi-key-fill"></i>
                              <span>Generate New Token</span>
                            </>
                          )}
                        </button>

                        {clientData?.status !== 'active' && (
                          <div className="status-warning">
                            <i className="bi bi-exclamation-triangle"></i>
                            <span>Client must be active to generate tokens</span>
                          </div>
                        )}
                      </div>

                      {token && (
                        <div className="token-display">
                          <label className="modern-label">Generated API Token</label>
                          <div className="token-container">
                            <div className="token-input-group">
                              <input
                                type="text"
                                value={token}
                                readOnly
                                className="token-input"
                              />
                              <button
                                className="copy-btn primary"
                                onClick={handleCopyToken}
                                title="Copy API Token"
                              >
                                <i className="bi bi-clipboard-check"></i>
                                Copy
                              </button>
                            </div>
                          </div>

                          <div className="token-security-notice">
                            <div className="security-icon">
                              <i className="bi bi-shield-exclamation"></i>
                            </div>
                            <div className="security-text">
                              <strong>Keep this token secure!</strong> Do not share it or expose it in client-side code.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-client-warning">
                    <div className="warning-icon">
                      <i className="bi bi-exclamation-triangle"></i>
                    </div>
                    <div className="warning-content">
                      <h4>No Client Found</h4>
                      <p>No client credentials found for your account. Please contact an administrator to set up your API access.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Usage & Documentation Card */}
            {clientData && (
              <div className="modern-card">
                <div className="card-header">
                  <div className="card-header-content">
                    <div className="card-icon usage">
                      <i className="bi bi-graph-up"></i>
                    </div>
                    <div>
                      <h3>API Usage & Documentation</h3>
                      <p>Monitor your usage and learn how to integrate</p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Usage Statistics */}
                  <div className="usage-section">
                    <div className="section-header">
                      <h4>Current Usage</h4>
                      <div className="usage-period">This Month</div>
                    </div>

                    <div className="usage-stats">
                      <div className="usage-numbers">
                        <span className="usage-current">{clientData.usageCount}</span>
                        <span className="usage-separator">/</span>
                        <span className="usage-limit">{clientData.usageQuota}</span>
                        <span className="usage-label">API calls</span>
                      </div>

                      <div className="usage-bar">
                        <div className="progress-track">
                          <div
                            className={`progress-fill ${getProgressBarColor()}`}
                            style={{ width: `${calculateUsagePercentage()}%` }}
                          ></div>
                        </div>
                        <div className="usage-percentage">
                          {calculateUsagePercentage()}%
                        </div>
                      </div>

                      <div className="usage-reset">
                        <i className="bi bi-arrow-clockwise"></i>
                        <span>Resets on {formatDate(clientData.resetDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Authentication Guide */}
                  <div className="auth-guide-section">
                    <div className="section-header">
                      <h4>Authentication</h4>
                    </div>

                    <p className='text-white'>Include your API token in the Authorization header of your requests:</p>

                    <div className="code-block">
                      <div className="code-header">
                        <span className="code-language">HTTP Header</span>
                        <button
                          className="code-copy"
                          onClick={() => {
                            navigator.clipboard.writeText('Authorization: Bearer YOUR_API_TOKEN');
                            setSuccess('Authorization header copied to clipboard');
                          }}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                      <pre className="code-content">
                        <code>Authorization: Bearer YOUR_API_TOKEN</code>
                      </pre>
                    </div>
                  </div>

                  {/* Client Status */}
                  <div className="status-section">
                    <div className="section-header">
                      <h4>Client Information</h4>
                    </div>

                    <div className="status-grid">
                      <div className="status-item">
                        <div className="status-label">Status</div>
                        <div className={`status-badge ${clientData.status}`}>
                          <div className="status-indicator-dot"></div>
                          {clientData.status}
                        </div>
                      </div>

                      <div className="status-item">
                        <div className="status-label">Last Activity</div>
                        <div className="status-value">
                          {formatDate(clientData.lastUsedAt) || 'Never'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Functions Card */}
            {isAdmin && (
              <div className="modern-card admin-card">
                <div className="card-header">
                  <div className="card-header-content">
                    <div className="card-icon admin">
                      <i className="bi bi-shield-check"></i>
                    </div>
                    <div>
                      <h3>Admin Functions</h3>
                      <p>Administrative tools and settings</p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="admin-notice">
                    <div className="notice-icon">
                      <i className="bi bi-info-circle"></i>
                    </div>
                    <div className="notice-content">
                      As an admin user, you have additional privileges including client management and system monitoring capabilities.
                    </div>
                  </div>

                  <div className="admin-actions">
                    <button
                      className="admin-btn primary"
                      onClick={() => window.location.href = '/admin'}
                    >
                      <i className="bi bi-speedometer2"></i>
                      <span>Admin Dashboard</span>
                    </button>

                    <button
                      className="admin-btn secondary"
                      onClick={() => window.location.href = '/admin/clients'}
                    >
                      <i className="bi bi-people"></i>
                      <span>Manage Clients</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default APITokenManagement;